import { getIdentity, useProfile } from '@sdc/auth';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { initialsOf } from '../../shared/network';
import { logAuditEvent } from '../lib/audit';
import { logoColor } from '../lib/logos';

/**
 * Platzhalter-Badges (Design-System §7). Die Vergabe-Logik liegt später
 * serverseitig in `badges`/`user_badges`; hier steht bewusst nur die Optik.
 */
const PLACEHOLDER_BADGES = [
  {
    key: 'early',
    tip: 'Early Adopter · Hub-Login in Woche 1',
    path: 'M8 1l2 4 4 .6-3 3 .8 4.4L8 11l-3.8 2 .8-4.4-3-3L6 5z',
    fill: '#F25A94',
  },
  {
    key: 'streak',
    tip: 'Streak · 14 Tage aktiv',
    path: 'M9 1L3 9h4l-1 6 7-9H8z',
    fill: '#00E6C7',
  },
  {
    key: 'clean',
    tip: 'Clean Data · 30 Tage ohne Pflichtfeld-Lücken',
    path: 'M8 1l6 2v4c0 4-2.6 6.8-6 8-3.4-1.2-6-4-6-8V3z',
    fill: '#8b5cf6',
  },
] as const;

export function Topbar({ compact = false }: { compact?: boolean }) {
  const { profile, isAdmin } = useProfile();
  const [email, setEmail] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let active = true;
    void getIdentity()
      .auth.getSession()
      .then(({ data }) => {
        if (active) setEmail(data.session?.user.email ?? null);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  async function logout() {
    setBusy(true);
    // Logout im Hub killt das geteilte Cookie -> wirkt sofort in allen Apps.
    await logAuditEvent('logout');
    await getIdentity().auth.signOut();
    navigate('/login', { replace: true });
  }

  const who = profile?.display_name ?? email;

  return (
    <header className="topbar">
      <Link to="/" className="topbar__brand">
        <img src={logoColor} alt="" width={34} height={34} />
        <span className="topbar__title">
          SDC <em>HUB</em>
        </span>
      </Link>

      <span className="topbar__spacer" />

      {!compact && (
        <div className="badges" aria-label="Deine Auszeichnungen">
          {PLACEHOLDER_BADGES.map((badge) => (
            <span className="badges__item" key={badge.key} tabIndex={0} role="img" aria-label={badge.tip}>
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path d={badge.path} fill={badge.fill} />
              </svg>
              <span className="badges__tip">{badge.tip}</span>
            </span>
          ))}
          <span className="badges__more">+4</span>
        </div>
      )}

      <button
        type="button"
        className="avatar"
        onClick={() => setMenuOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={who ? `Konto: ${who}` : 'Konto'}
        title={who ?? 'Konto'}
      >
        {initialsOf(profile?.display_name ?? email)}
      </button>

      {menuOpen && (
        <div className="usermenu" role="menu" ref={menuRef}>
          <div className="usermenu__who">{who ?? 'angemeldet'}</div>
          {isAdmin && (
            <Link className="usermenu__item" role="menuitem" to="/admin" onClick={() => setMenuOpen(false)}>
              Admin
            </Link>
          )}
          <Link
            className="usermenu__item"
            role="menuitem"
            to="/set-password"
            onClick={() => setMenuOpen(false)}
          >
            Passwort ändern
          </Link>
          <button
            type="button"
            className="usermenu__item"
            role="menuitem"
            onClick={logout}
            disabled={busy}
          >
            Abmelden
          </button>
        </div>
      )}
    </header>
  );
}
